import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import useSeo from "@/seo/useSeo";
import { ARTICLES, BY_SLUG } from "@/content/articles";

const Block = ([kind, value]) => {
  if (kind === "h2") return <h2 className="font-display text-2xl text-bone sm:text-3xl">{value}</h2>;
  if (kind === "h3") return <h3 className="font-display text-lg text-brass md:text-xl">{value}</h3>;
  if (kind === "p") return <p className="text-sm leading-relaxed text-muted-foreground sm:text-base">{value}</p>;
  if (kind === "ul")
    return (
      <ul className="space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
        {value.map((li) => <li key={li} className="list-disc marker:text-brass">{li}</li>)}
      </ul>
    );
  if (kind === "note")
    return (
      <p className="border-l-2 border-brass/50 bg-white/[0.02] py-3 pl-5 text-sm">
        <span className="text-muted-foreground">À lire aussi : </span>
        <Link to={value.to} className="text-brass underline underline-offset-4 hover:text-bone">{value.label}</Link>
      </p>
    );
  return (
    <p className="pt-2">
      <Link to={value.to} data-testid="article-cta"
        className="mono-label inline-flex items-center gap-3 border border-brass px-7 py-4 text-brass transition-colors hover:bg-brass hover:text-ink">
        {value.label} <ArrowRight size={14} />
      </Link>
    </p>
  );
};

export default function Article() {
  const { slug } = useParams();
  useSeo(`/decouvrir-haut-doubs/${slug}`);
  const a = BY_SLUG[slug];
  if (!a) return <Navigate to="/decouvrir-haut-doubs" replace />;
  const others = ARTICLES.filter((x) => x.slug !== slug).slice(0, 2);

  return (
    <div className="bg-ink pb-28 pt-32 sm:pt-40" data-testid={`article-${slug}`}>
      <div className="mx-auto max-w-3xl px-6">
        <Link to="/decouvrir-haut-doubs" data-testid="back-to-discover"
          className="mono-label inline-flex items-center gap-2 text-brass hover:text-bone">
          <ArrowLeft size={14} /> Découvrir le Haut-Doubs
        </Link>
        <h1 className="display-xl mt-8 text-3xl leading-tight text-bone sm:text-4xl lg:text-5xl">{a.h1}</h1>
        <img src={a.image} alt={a.alt} decoding="async"
          className="mt-12 h-64 w-full object-cover sm:h-96" />
        <div className="mt-14 space-y-6">
          {a.blocks.map((b, i) => <div key={i}>{Block(b)}</div>)}
        </div>
        {a.credit && (
          <div className="mt-16 border-t border-white/10 pt-6" data-testid="article-photo-credits">
            <p className="mono-label text-brass">Crédits photos</p>
            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
              «&nbsp;
              <a href={a.credit.source} target="_blank" rel="noopener noreferrer nofollow"
                className="underline underline-offset-4 hover:text-brass">{a.credit.title}</a>
              &nbsp;» — {a.credit.author}, sous licence{" "}
              <a href={a.credit.licenseUrl} target="_blank" rel="noopener noreferrer nofollow"
                className="underline underline-offset-4 hover:text-brass">{a.credit.license}</a>
              , via Wikimedia Commons
              {a.credit.modified ? " — image redimensionnée et recadrée pour ce site." : "."}
            </p>
          </div>
        )}
      </div>

      <div className="mx-auto mt-24 max-w-6xl border-t border-white/10 px-6 pt-16">
        <p className="mono-label text-brass">À découvrir aussi</p>
        <div className="mt-10 grid gap-10 sm:grid-cols-2">
          {others.map((o) => (
            <Link key={o.slug} to={`/decouvrir-haut-doubs/${o.slug}`} data-testid={`related-${o.slug}`}
              className="group border border-white/10 bg-white/[0.02] p-7 transition-colors hover:border-brass/40">
              <h2 className="font-display text-lg text-bone group-hover:text-brass md:text-xl">{o.h1}</h2>
              <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{o.card}</p>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
